/**
 * Tabla read-only de las plantaciones de un `AuditPlot` o `PlotReal`.
 *
 * En una parcela sin doblar la tabla no aporta valor sobre los campos
 * legacy del padre — el caller decide si pintarla o no según el flag
 * `is_doubled`. La razón de que exista este componente es dar soporte
 * visual a las **parcelas dobladas**: 2+ plantings coexistiendo con
 * distinta variedad, año, marco o plantas, sea en modo total
 * (comparten geometría) o parcial (cada una con `sub_geom` propio).
 *
 * Read-only en 2026-07: la edición de plantings se hace desde la app
 * móvil (wizard PlotDataStep). Un CTA "Editar en móvil" al pie recuerda
 * al admin/coord que no puede tocarlos desde web todavía.
 */

import { useState } from "react";
import {
  App as AntdApp,
  Button,
  Empty,
  Popconfirm,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import type { AuditPlotPlanting } from "@/types/audit";
import { auditPlotPlantingService } from "@/services/api/auditPlotPlantingService";
import { PlantingEditModal } from "./PlantingEditModal";

const { Text } = Typography;

interface Props {
  plantings: AuditPlotPlanting[];
  /** Indica si al menos una plantación tiene `sub_geom` — el header
   *  muestra un chip "Parcial" para que el revisor entienda que la
   *  superficie no es compartida. Cuando todas comparten geometría
   *  con el padre es "Total". */
  showModeChip?: boolean;
  /** Modo edición (admin/coord tras la auditoría). Activa botones
   *  Añadir, Editar y Eliminar en cada fila.  */
  editable?: boolean;
  /** ID de la parcela auditada — obligatorio en modo editable para
   *  crear plantings nuevos (asociándolos al parent correcto). */
  auditPlotId?: string;
  /** Callback tras cualquier cambio (create/update/delete). El caller
   *  suele recargar `plot` completo para reflejar el nuevo `is_doubled`
   *  y `plantings[]`. */
  onChange?: () => void;
}

export const PlantingsTable: React.FC<Props> = ({
  plantings,
  showModeChip = true,
  editable = false,
  auditPlotId,
  onChange,
}) => {
  const { message } = AntdApp.useApp();
  const [editingPlanting, setEditingPlanting] =
    useState<AuditPlotPlanting | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handleAdd = () => {
    setEditingPlanting(null);
    setModalOpen(true);
  };

  const handleEdit = (row: AuditPlotPlanting) => {
    setEditingPlanting(row);
    setModalOpen(true);
  };

  const handleDelete = async (row: AuditPlotPlanting) => {
    try {
      await auditPlotPlantingService.remove(row.id);
      message.success("Plantación eliminada.");
      onChange?.();
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } }).response?.data
          ?.detail ?? (err as Error).message;
      message.error(`Error: ${detail}`);
    }
  };

  const suggestedOrdering =
    plantings.length === 0
      ? 1
      : Math.max(...plantings.map((p) => p.ordering)) + 1;
  if (!plantings || plantings.length === 0) {
    return <Empty description="Sin plantaciones registradas" />;
  }

  const isPartial = plantings.some((p) => p.sub_geom != null);

  const columns: ColumnsType<AuditPlotPlanting> = [
    {
      title: "#",
      dataIndex: "ordering",
      width: 50,
      render: (n: number) => <Text strong>{n}</Text>,
    },
    {
      title: "Variedad",
      dataIndex: "variety_name",
      render: (name: string | null, row) => name ?? row.variety_code ?? "—",
    },
    {
      title: "Patrón",
      dataIndex: "pattern_name",
      render: (name: string | null) => name ?? "—",
    },
    {
      title: "Tipo",
      dataIndex: "plot_type_name",
      render: (name: string | null) => name ?? "—",
    },
    {
      title: "Año",
      dataIndex: "crop_date",
      width: 80,
      render: (year: number | null) => year ?? "—",
    },
    {
      title: "Marco (fila × col)",
      width: 130,
      render: (_, row) => {
        if (row.plantation_frame_row == null || row.plantation_frame_column == null) {
          return "—";
        }
        return `${row.plantation_frame_row} × ${row.plantation_frame_column} m`;
      },
    },
    {
      title: "Plantas",
      dataIndex: "plants",
      width: 90,
      render: (n: number | null) =>
        n != null ? n.toLocaleString("es-ES") : "—",
    },
    {
      title: "Superficie",
      dataIndex: "surface",
      width: 110,
      render: (s: number | null, row) => {
        if (s == null) return "—";
        return (
          <span>
            {s.toFixed(4)} ha
            {row.sub_geom ? (
              <Tag color="orange" style={{ marginLeft: 6 }}>
                sub-geom
              </Tag>
            ) : null}
          </span>
        );
      },
    },
    {
      title: "Injertos",
      dataIndex: "grafting",
      width: 90,
      render: (n: number | null) => (n != null ? n : "—"),
    },
  ];

  if (editable) {
    columns.push({
      title: "",
      width: 100,
      align: "right" as const,
      render: (_, row) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(row)}
          />
          {/* Sólo permitimos borrar plantings NO principales — el
              backend valida ordering != 1, pero deshabilitamos aquí
              también para no mostrar un CTA que va a rebotar. */}
          <Popconfirm
            title="¿Eliminar plantación?"
            description={
              row.ordering === 1
                ? "La plantación principal no se puede eliminar por separado."
                : "Esta acción no se puede deshacer."
            }
            onConfirm={() => handleDelete(row)}
            okText="Eliminar"
            okButtonProps={{
              danger: true,
              disabled: row.ordering === 1,
            }}
            cancelText="Cancelar"
            disabled={row.ordering === 1}
          >
            <Button
              size="small"
              danger
              icon={<DeleteOutlined />}
              disabled={row.ordering === 1}
            />
          </Popconfirm>
        </Space>
      ),
    });
  }

  return (
    <div>
      {showModeChip ? (
        <div style={{ marginBottom: 8, display: "flex", alignItems: "center" }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Modo de doblado:{" "}
          </Text>
          {isPartial ? (
            <Tag color="orange">Parcial (con sub-geometrías)</Tag>
          ) : (
            <Tag color="blue">Total (superficie compartida)</Tag>
          )}
          <Text type="secondary" style={{ fontSize: 12, marginLeft: 12 }}>
            {plantings.length} plantaci
            {plantings.length === 1 ? "ón" : "ones"}
          </Text>
          {editable && auditPlotId ? (
            <Button
              size="small"
              type="primary"
              icon={<PlusOutlined />}
              style={{ marginLeft: "auto" }}
              onClick={handleAdd}
            >
              Añadir plantación
            </Button>
          ) : null}
        </div>
      ) : null}
      <Table<AuditPlotPlanting>
        rowKey="id"
        dataSource={[...plantings].sort((a, b) => a.ordering - b.ordering)}
        columns={columns}
        pagination={false}
        size="small"
        bordered
      />
      {!editable ? (
        <Text
          type="secondary"
          style={{ fontSize: 11, display: "block", marginTop: 8 }}
        >
          Para editar las plantaciones usa la app móvil (wizard de datos
          de parcela).
        </Text>
      ) : (
        <Text
          type="secondary"
          style={{ fontSize: 11, display: "block", marginTop: 8 }}
        >
          Añade, edita o elimina plantaciones (`sub_geom` sigue
          editándose desde la app móvil).
        </Text>
      )}

      {editable && auditPlotId && modalOpen ? (
        <PlantingEditModal
          auditPlotId={auditPlotId}
          planting={editingPlanting}
          suggestedOrdering={suggestedOrdering}
          onCancel={() => setModalOpen(false)}
          onDone={() => {
            setModalOpen(false);
            onChange?.();
          }}
        />
      ) : null}
    </div>
  );
};
